import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { doc, getDoc } from "firebase/firestore";
import { runtimeDb as db } from "../../firestoreStudio/runtime/runtimeDb";
import { fetchUndoableOperations, restoreOperationBackups } from "../services/undoService";
import { RestorePreviewModal } from "../components/OperationWorkflow";
import { writeAuditLog } from "../services/auditLogService";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";

export default function UndoCenterPage() {
  const { user } = useFirestoreAdmin();
  const [operations, setOperations] = useState([]);
  const [backups, setBackups] = useState([]);
  const [preview, setPreview] = useState(null);
  const [currentDoc, setCurrentDoc] = useState(null);

  const load = async () => {
    const data = await fetchUndoableOperations();
    setOperations(data.operations);
    setBackups(data.backups);
  };

  useEffect(() => {
    load();
  }, []);

  const openRestorePreview = async (backup) => {
    const snap = await getDoc(doc(db, backup.collection, backup.firestoreDocId));
    setCurrentDoc(snap.exists() ? snap.data() : {});
    setPreview(backup);
  };

  const restoreOne = async () => {
    if (!preview) return;
    try {
      await restoreOperationBackups([preview]);
      await writeAuditLog({
        adminName: user?.email,
        adminUid: user?.uid,
        action: "restore_undo",
        collection: preview.collection,
        documentIds: [preview.firestoreDocId],
        oldValues: preview.data,
      });
      toast.success("Restored");
      setPreview(null);
      load();
    } catch (e) {
      toast.error(e?.message || "Restore failed");
    }
  };

  const restoreAllForOp = async (op) => {
    const related = backups.filter(
      (b) => b.collection === op.collection && b.createdAt <= op.dateTime && b.createdAt >= new Date(new Date(op.dateTime).getTime() - 3600000).toISOString()
    );
    if (!related.length) return toast.error("No backups found for this operation");
    const { restored, failed } = await restoreOperationBackups(related);
    toast.success(`Restored ${restored}, failed ${failed}`);
    load();
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-bold">Undo Center</h1>
        <p className="text-sm text-slate-600">Recent updates, deletes, and bulk operations — restore from firestore_backups.</p>
      </header>
      <div className="fa-card fa-table-wrap">
        <table className="fa-table">
          <thead>
            <tr>
              <th>Operation</th>
              <th>Collection</th>
              <th>Documents</th>
              <th>Time</th>
              <th>Admin</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {operations.map((op) => (
              <tr key={op.id}>
                <td>{op.action}</td>
                <td>{op.collection}</td>
                <td>{(op.documentIds || []).length || "—"}</td>
                <td className="text-xs">{op.dateTime}</td>
                <td className="text-xs">{op.adminName}</td>
                <td>
                  <button type="button" className="fa-btn fa-btn-primary text-xs" onClick={() => restoreAllForOp(op)}>
                    Restore all
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="fa-card fa-table-wrap">
        <h2 className="border-b p-3 font-semibold">Recent backups</h2>
        <table className="fa-table">
          <thead>
            <tr>
              <th>Collection</th>
              <th>Document</th>
              <th>Operation</th>
              <th>Time</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {backups.slice(0, 50).map((b) => (
              <tr key={b.id}>
                <td>{b.collection}</td>
                <td className="font-mono text-xs">{b.firestoreDocId}</td>
                <td>{b.operation}</td>
                <td className="text-xs">{b.createdAt}</td>
                <td>
                  <button type="button" className="fa-btn text-xs" onClick={() => openRestorePreview(b)}>Preview & restore</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <RestorePreviewModal
        open={!!preview}
        oldDoc={preview?.data}
        currentDoc={currentDoc}
        onClose={() => setPreview(null)}
        onRestore={restoreOne}
      />
    </div>
  );
}
